import React, { useState, useMemo, useCallback, useEffect } from 'react';

import { Iframe, getModalDimensions } from '../../custom-ui';
import {
	type ModalIframeRendererProps,
	type CustomUIRendererProps,
	type InternalModalState,
	type EmbeddedUIKitModalContext,
} from './types';
import Image from '@atlaskit/image';
import Modal, { ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { FullScreenModalDialog } from '@atlaskit/modal-dialog/full-screen';
import { cssMap } from '@atlaskit/css';
import { useIsReducedMotion } from '@atlaskit/motion/accessibility';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ConfluenceExtension, JiraExtension } from '@atlassian/forge-ui-types';
import { getAtlasKitModalWidth, ModalContentWrapper } from './custom-ui-modal-utils';
import { ForgeModalDeprecationMessage } from '../ForgeModalDeprecationMessage';
import { ForgeDeprecationMessage, SANDBOX_DEPRECATION_ERROR } from '../ForgeDeprecationMessage';
import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import AddonIcon from '@atlaskit/icon/core/app';
import { useForgeUiAnalyticsEvent } from '../../analytics/useForgeUiAnalyticsEvent';
import { getExtensionType } from '../../utils/getExtensionType';
import { EmbeddedUIKitModal } from './EmbeddedUIKitModal';

const getExtensionIcon = (extension: {
	properties?: { config?: { icon?: string }; icon?: string };
}): string | undefined => {
	return extension?.properties?.config?.icon ?? extension?.properties?.icon;
};

const styles = cssMap({
	modalDeprecationMessage: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
});

type Props = CustomUIRendererProps & {
	isInModal?: boolean;
	// These are internal props, not exposed to the product
	onModalCustomUIRendererClose?: () => Promise<void>;
	setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const CustomUIRenderer = ({
	accountId,
	bridge,
	client,
	contextIds,
	coreData,
	customBridgeMethods,
	dialogs,
	entryPoint,
	extension,
	extensionData,
	extensionPayload,
	extensionViewData,
	getContextToken,
	getThreeLOPrompt,
	height,
	width,
	isIframeHidden,
	isIframeResizable,
	iframeRef,
	isInModal,
	loadingComponent,
	locale,
	localId,
	modalStyles,
	modalProvider,
	onIframeLoad,
	onModalCustomUIRendererClose,
	product,
	timezone,
	getModalWidth,
	setIsLoading,
	isEmbeddedMacro,
	embeddedParentLocalId,
}: Props): React.JSX.Element => {
	const [error, setError] = useState<string | null>(null);
	// modalOptions is null when no modal is open is non null when opened
	// Uses InternalModalState to allow extension property from FAUS forwarding
	const [modalOptions, setModalOptions] = useState<InternalModalState | null>(null);
	const [viewOnClose, setViewOnClose] = useState<() => Promise<void> | void>();

	const [embeddedUIKitModalContext, setEmbeddedUIKitModalContext] =
		useState<EmbeddedUIKitModalContext | null>(null);

	const onCloseFromModalClass = modalOptions?.onClose;
	const embeddedMacroModalProps = modalOptions?.isEmbeddedMacroModal
		? {
				extensionPayload: modalOptions?.extensionPayload,
				// Extension view data in macro module is only reserved for bodied macro content.
				// Since we don't support embedded bodied macros, extensionViewData will always be undefined.
				extensionViewData: undefined,
				coreData: {
					...coreData,
					localId: modalOptions?.embeddedMacroLocalId || '',
				},
			}
		: {};

	const iframeProps = {
		accountId,
		apolloClient: client,
		contextIds,
		components: getThreeLOPrompt,
		coreData,
		customBridgeMethods,
		extension,
		extensionData,
		extensionPayload,
		extensionViewData,
		getContextToken,
		isHidden: isIframeHidden,
		isResizable: isIframeResizable,
		locale,
		timezone,
		loadingComponent,
		isEmbeddedMacro,
		embeddedParentLocalId,
		...(fg('platform_faus_navigate_to_product') ? {} : embeddedMacroModalProps),
	};
	const modalIframeProps = {
		...iframeProps,
		...embeddedMacroModalProps,
	};

	const isBodiedMacro = extension.properties?.layout === 'bodied';

	const extensionInModal = useMemo(() => {
		// Use the embedded macro's extension if provided (for modals opened from embedded macros)
		const baseExtension = modalOptions?.extension || extension;
		return {
			...baseExtension,
			// Pass a type that is not included in the dimensions presets so that it falls back to default
			type: `${baseExtension.type}::dynamic-modal`,
			properties: {
				...baseExtension.properties,
				resource: modalOptions?.resource ?? baseExtension.properties.resource,
			},
		};
	}, [extension, modalOptions?.resource, modalOptions?.extension]);

	const onError = (error: string | undefined) => {
		error && setError(error);
	};

	const extensionDataInModal = useMemo(() => {
		// Use the embedded macro's extensionData if provided (for modals opened from embedded macros)
		const baseExtensionData = modalOptions?.extensionData || extensionData;
		return {
			...baseExtensionData,
			...(modalOptions?.context ? { modal: modalOptions?.context } : {}),
			// This is to handle an incompatibility issue with Compass and JSM where they are incorrectly currently spreading the context object on the top level instead of in the `modal` object.
			// Remove once Compass apps have been updated https://softwareteams.atlassian.net/browse/COMPASS-22051 and a notice to app developers have been sent out
			...((product === 'compass' || extension.type.startsWith('jiraServiceManagement')) &&
			modalOptions?.context
				? { ...modalOptions?.context }
				: {}),
		};
	}, [extension.type, extensionData, modalOptions?.context, modalOptions?.extensionData, product]);

	const handleModalTearDown = useCallback(() => {
		setModalOptions(null);
	}, [setModalOptions]);

	const isReducedMotion = useIsReducedMotion();

	const handleModalClose = useCallback(() => {
		onCloseFromModalClass?.();
		// if reduced motion is enabled, we need to call handleModalTearDown here as it no longer calls `onCloseComplete` in `ModalCustomUIRenderer`
		if (isReducedMotion) {
			handleModalTearDown();
		}
	}, [isReducedMotion, onCloseFromModalClass, handleModalTearDown]);

	// Confluence specific feature
	// Needed to show Forge modals in live pages. dialogs won't be passed in if the page isn't live.
	useEffect(() => {
		if (product !== 'confluence') {
			return;
		}
		if (dialogs && modalOptions) {
			dialogs.showDialog(ModalCustomUIRenderer, {
				extension: extensionInModal,
				extensionData: extensionDataInModal,
				localId,
				onTearDown: handleModalTearDown,
				bridge,
				size: modalOptions.size,
				closeOnEscape: modalOptions.closeOnEscape,
				closeOnOverlayClick: modalOptions.closeOnOverlayClick,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modalOptions, dialogs]);

	if (error) {
		// Sandbox deprecation error
		if (error === SANDBOX_DEPRECATION_ERROR) {
			return <ForgeDeprecationMessage extension={extension} error={error} />;
		}

		// Currently no other error types are handled
	}

	return (
		<>
			{isBodiedMacro &&
			embeddedUIKitModalContext?.forgeDoc &&
			embeddedUIKitModalContext?.extension &&
			fg('platform_forge_ui_uikit_modal_in_custom_ui_parent') ? (
				<EmbeddedUIKitModal
					accountId={accountId}
					bridge={bridge}
					client={client}
					coreData={coreData}
					customBridgeMethods={customBridgeMethods}
					forgeDoc={embeddedUIKitModalContext.forgeDoc}
					extension={embeddedUIKitModalContext.extension}
					getThreeLOPrompt={getThreeLOPrompt}
					loadingComponent={loadingComponent}
					product={product}
					isEmbeddedMacro={isEmbeddedMacro}
					embeddedParentLocalId={embeddedParentLocalId}
				/>
			) : null}
			<Iframe
				{...iframeProps}
				entryPoint={entryPoint}
				isResizable={isIframeResizable}
				bridge={{
					...bridge,
					openModal: (opts) => {
						setModalOptions(opts.data);
						return true;
					},
					...(onModalCustomUIRendererClose
						? {
								close: (payload: any) => {
									if (fg('forge-ui-on-close-bridge-method')) {
										onModalCustomUIRendererClose()
											.then(() => {
												bridge?.close?.(payload);
											})
											.finally(() => {
												return true;
											});
									} else {
										// This bridge.close reference is defined below in ModalCustomUIRenderer.
										onModalCustomUIRendererClose();
										bridge?.close?.(payload);
										return true;
									}
								},
							}
						: {}),
				}}
				customBridgeMethods={{
					...customBridgeMethods,
					...(isBodiedMacro && fg('platform_forge_ui_uikit_modal_in_custom_ui_parent')
						? {
								setEmbeddedMacroModalForgeDoc: (payload: {
									embeddedUIKitModalContext: EmbeddedUIKitModalContext;
								}) => {
									setEmbeddedUIKitModalContext(payload?.embeddedUIKitModalContext);
								},
							}
						: {}),
				}}
				height={height}
				width={width}
				isInModal={isInModal}
				ref={iframeRef}
				onLoad={onIframeLoad}
				setIsLoading={setIsLoading}
				onError={onError}
			/>
			{modalOptions ? (
				<ModalCustomUIRenderer
					{...(fg('platform_faus_navigate_to_product') ? modalIframeProps : iframeProps)}
					product={product}
					getThreeLOPrompt={getThreeLOPrompt}
					dialogs={dialogs}
					client={client}
					extension={extensionInModal}
					extensionData={extensionDataInModal}
					localId={modalOptions?.embeddedMacroLocalId || localId}
					onTearDown={handleModalTearDown}
					bridge={{
						...bridge,
						onClose: (callback) => {
							setViewOnClose(() => callback.data);
							// stores the callback for when modal is closed
							return true;
						},
						close: (payload: any) => {
							onCloseFromModalClass?.(payload.data);
							return true;
						},
					}}
					modalOnClose={handleModalClose}
					size={modalOptions.size}
					title={modalOptions.title}
					icon={modalOptions.icon}
					closeOnEscape={modalOptions.closeOnEscape}
					closeOnOverlayClick={modalOptions.closeOnOverlayClick}
					modalStyles={modalStyles}
					modalProvider={modalProvider}
					getModalWidth={getModalWidth}
					isIframeResizable={isIframeResizable}
					viewOnClose={viewOnClose}
				/>
			) : null}
		</>
	);
};

export const ModalCustomUIRenderer = ({
	accountId,
	bridge,
	coreData,
	client,
	closeOnEscape,
	closeOnOverlayClick,
	consentMessage,
	contextIds,
	customBridgeMethods,
	dialogs,
	entryPoint,
	extension,
	extensionData,
	extensionPayload,
	extensionViewData,
	getContextToken,
	getThreeLOPrompt,
	isIframeHidden = false,
	isIframeResizable = false,
	loadingComponent,
	locale,
	localId,
	mentionProvider,
	modalExtension,
	modalStyles,
	modalProvider: ModalProvider,
	onConsentModalClose,
	onConsentSuccess,
	onError,
	onForgeDocUpdated,
	onIframeLoad,
	onTearDown,
	product,
	size,
	title,
	icon,
	timezone,
	getModalWidth,
	// onClose from `Modal`
	modalOnClose,
	// onClose from `view`
	viewOnClose,
}: ModalIframeRendererProps): React.JSX.Element => {
	const [isOpen, setIsOpen] = useState(true);

	// This is for preventing inline dialog apps from being closed by an esc key press when a
	// modal is open until https://product-fabric.atlassian.net/browse/DSP-3552 is resolved.
	useEffect(() => {
		if (
			product !== 'confluence' ||
			modalExtension?.type === 'macroConfig' ||
			(product === 'confluence' && modalExtension?.type === 'bylineItem')
		) {
			return;
		}
		function dismissEscape(e: any) {
			if (e.key === 'Escape') {
				e.stopPropagation();

				if (closeOnEscape) {
					modalOnClose?.();
					setIsOpen(false);
				}
			}
		}

		if (isOpen) {
			document.addEventListener('keydown', dismissEscape, { capture: true });
		}

		return () =>
			document.removeEventListener('keydown', dismissEscape, {
				capture: true,
			});
	}, [isOpen, closeOnEscape, product, modalOnClose, modalExtension?.type]);

	const { trackExtensionLoaded, trackBridgeSucceed, trackBridgeFailed } =
		useForgeUiAnalyticsEvent();

	const extensionType = getExtensionType(extension?.properties);
	const environmentType = extension?.environmentType;

	// Only tracking fullscreen modals
	useEffect(() => {
		if (
			modalExtension?.modalWidth === 'fullscreen' &&
			fg('forge_enable_fullscreen_viewport_for_custom_config')
		) {
			trackExtensionLoaded({
				forgeEnvironment: environmentType,
				renderType: 'CustomUI',
				extensionType: extensionType,
				viewportSize: 'fullscreen',
			});
		}
	}, [modalExtension?.modalWidth, environmentType, extensionType, trackExtensionLoaded]);

	const modalWidthForMacroCustomConfig =
		extension.type === ConfluenceExtension.MACRO &&
		fg('forge_enable_fullscreen_viewport_for_custom_config')
			? modalExtension?.modalWidth
			: undefined;

	const modalSize =
		modalWidthForMacroCustomConfig ?? size ?? extension.properties.viewportSize ?? 'medium';
	const modalWidth = getModalWidth ? getModalWidth(size) : getAtlasKitModalWidth(modalSize);

	// If size is defined, iframe modal should not be resizable.
	// In JSM ONLY, iframe modal should ALWAYS be resizable.
	// Iframe should scale to 100% of the modal so that Select drop downs are not cut off
	const isJsmModule = extension.type.startsWith('jiraServiceManagement');
	const isModalResizable = isJsmModule || (size !== undefined ? false : isIframeResizable);

	const shouldShowFCFModalDeprecationMessage =
		(extension.type === JiraExtension.CUSTOM_FIELD ||
			extension.type === JiraExtension.CUSTOM_FIELD_TYPE) &&
		!fg('forge_disable_fcf_modal_experience_on_issue_view');

	const executeViewOnCloseCallback = async () => {
		if (viewOnClose && fg('forge-ui-on-close-bridge-method')) {
			try {
				await viewOnClose();
				trackBridgeSucceed({
					method: 'onClose',
					forgeEnvironment: extension.environmentType,
				});
			} catch (error) {
				trackBridgeFailed({
					method: 'onClose',
					errorName: error instanceof Error ? error.name : 'UnknownError',
					forgeEnvironment: extension.environmentType,
				});
			}
		}
	};

	const renderModal = () => {
		const closeProps = {
			onClose: async () => {
				try {
					await executeViewOnCloseCallback();
				} finally {
					modalOnClose?.();
					setIsOpen(false);
				}
			},
			onCloseComplete: onTearDown,
		};

		const modalContent = (
			<>
				{shouldShowFCFModalDeprecationMessage && (
					<Box xcss={styles.modalDeprecationMessage}>
						<ForgeModalDeprecationMessage extension={extension} />
					</Box>
				)}
				<ModalContentWrapper
					size={modalSize}
					styles={modalStyles}
					isResizable={
						fg('forge-ui-fix-modal-resizing-in-jsm') ? isModalResizable : isIframeResizable
					}
				>
					<CustomUIRenderer
						product={product}
						coreData={coreData}
						getThreeLOPrompt={getThreeLOPrompt}
						dialogs={dialogs}
						accountId={accountId}
						client={client}
						contextIds={contextIds}
						extension={extension}
						extensionData={extensionData}
						extensionViewData={extensionViewData}
						bridge={bridge}
						customBridgeMethods={customBridgeMethods}
						consentMessage={consentMessage}
						entryPoint={entryPoint}
						getContextToken={getContextToken}
						height="100%"
						isIframeHidden={isIframeHidden}
						isIframeResizable={isModalResizable}
						locale={locale}
						localId={localId}
						mentionProvider={mentionProvider}
						modalExtension={modalExtension}
						onConsentSuccess={onConsentSuccess}
						onForgeDocUpdated={onForgeDocUpdated}
						timezone={timezone}
						onConsentModalClose={onConsentModalClose}
						onError={onError}
						onIframeLoad={onIframeLoad}
						loadingComponent={loadingComponent}
						extensionPayload={extensionPayload}
						modalStyles={modalStyles}
						// This is called when `view.close` is called
						onModalCustomUIRendererClose={async () => {
							try {
								await executeViewOnCloseCallback();
							} finally {
								setIsOpen(false);
								// If is a modal extension point, call modalOnClose to reset state from product
								// This is required to reset the state of the modal in the product when view.close is called
								if (modalExtension) {
									modalOnClose?.();
								}
							}
						}}
						isInModal={true}
					/>
				</ModalContentWrapper>
			</>
		);

		/**
		 * Logic for fullscreen modals as follows:
		 * - Only available for bylineItem and macro custom config extension points
		 * - Available for Modal from @forge/bridge
		 */

		// Determine if this is a modal extension point vs bridge modal (Modal class from @forge/bridge)
		// Bridge modal: modalExtension is undefined (opened via view.modal.open())
		const isBridgeModal = !modalExtension;
		const isMacroExtension = extension.type === ConfluenceExtension.MACRO;
		const isBylineExtension = extension.type === ConfluenceExtension.BYLINE;

		/**
		 * Determines if the modal should render as fullscreen based on:
		 * - Modal size being set to 'fullscreen'
		 * - Appropriate feature flag for the extension type or bridge modal
		 * - Not a production environment during EAP (except for byline, which has already rolled out without the blocker)
		 */
		const supportsFullscreenModal = (() => {
			if (modalSize !== 'fullscreen') {
				return false;
			}

			// Bridge modals (opened via view.modal.open() with size: 'fullscreen')
			if (isBridgeModal) {
				return true;
			}

			// Extension point modals
			// Byline fullscreen modals rolled out to production Forge apps already
			if (isBylineExtension) {
				return true;
			}
			if (isMacroExtension) {
				return fg('forge_enable_fullscreen_viewport_for_custom_config');
			}

			return false;
		})();

		const renderModalHeader = () => {
			/* Title/icon source priority:
			 * - Bridge modals:
			 *     - Use title/icon props (passed in from bridge call)
			 *     - Fallback to extension.properties.title for fullscreen modals with empty title
			 *     - For non-fullscreen modals, we won't render the header if the title prop is not provided
			 * - Modal extension points: Use modalExtension.title and extensionIcon, defaults to extension name if not provided
			 */
			const modalTitle = isBridgeModal
				? modalSize === 'fullscreen' && !title
					? extension.properties.title
					: title
				: modalExtension?.title || extension.properties.title;

			/**
			 * Icon rendering logic:
			 * - Bridge modals: show icon section only if icon prop is provided
			 * - Macro extension points: always show icon section (uses extension icon or AddonIcon fallback)
			 * - Other modals: no icon section
			 */
			const shouldShowIcon = isBridgeModal ? !!icon : isMacroExtension;
			const modalIcon = isBridgeModal ? icon : getExtensionIcon(extension);

			return (
				<ModalHeader>
					{shouldShowIcon ? (
						<Inline space="space.100" alignBlock="center">
							{modalIcon ? (
								<Image src={modalIcon} alt="" width="24px" height="24px" />
							) : (
								<AddonIcon label="" color={token('color.icon.accent.gray')} />
							)}
							<ModalTitle>{modalTitle}</ModalTitle>
						</Inline>
					) : (
						<ModalTitle>{modalTitle}</ModalTitle>
					)}
					<IconButton
						appearance="subtle"
						icon={CrossIcon}
						label="Close Modal"
						onClick={
							isBridgeModal
								? closeProps.onClose
								: async () => {
										try {
											await executeViewOnCloseCallback();
										} finally {
											modalExtension?.closeModalExtension();
										}
									}
						}
					/>
				</ModalHeader>
			);
		};

		if (supportsFullscreenModal) {
			return (
				<FullScreenModalDialog
					{...closeProps}
					testId="custom-ui-fullscreen-modal-dialog"
					shouldCloseOnEscapePress={closeOnEscape}
				>
					{/** Must restrict to macros and bridge modal, as it would be a breaking change in byline to render this header */}
					{isMacroExtension || isBridgeModal ? renderModalHeader() : null}
					{modalContent}
				</FullScreenModalDialog>
			);
		} else {
			// Fallback to 'max' if modalSize is 'fullscreen' but fullscreen is not supported
			const shouldFallbackFromFullscreen = modalSize === 'fullscreen' && !supportsFullscreenModal;
			const fallbackSize = shouldFallbackFromFullscreen ? 'max' : modalSize;
			const fallbackWidth = shouldFallbackFromFullscreen ? '100%' : modalWidth;

			if (shouldFallbackFromFullscreen) {
				// eslint-disable-next-line no-console
				console.warn(
					'Forge fullscreen modal is in EAP. Please sign up here https://ecosystem.atlassian.net/servicedesk/customer/portal/1040/group/3496/create/18983. The modal will be rendered with max width instead.',
				);
			}
			const { height: modalHeight } = getModalDimensions(fallbackSize);

			return (
				<Modal
					{...closeProps}
					width={fallbackWidth}
					height={fallbackSize === 'max' ? modalHeight : undefined}
					testId="custom-ui-modal-dialog"
					shouldCloseOnOverlayClick={closeOnOverlayClick}
					shouldCloseOnEscapePress={closeOnEscape}
				>
					{isBridgeModal && title ? renderModalHeader() : null}
					{modalContent}
				</Modal>
			);
		}
	};

	if (ModalProvider) {
		return (
			<ModalTransition>{isOpen && <ModalProvider>{renderModal()}</ModalProvider>}</ModalTransition>
		);
	}

	return <ModalTransition>{isOpen && renderModal()}</ModalTransition>;
};
